import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import MusteriCagriListesi from './MusteriCagriListesi';
import { formatTelefonGoster } from '../utils/phoneNormalize';
import './GorusmeHubStep.css';

const formatTarih = (val) => {
  if (!val) return '—';
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' });
};

const formatTarihKisa = (val) => {
  if (!val) return '—';
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('tr-TR', { dateStyle: 'short' });
};

const GORUSME_DURUM_LABEL = {
  DevamEdiyor: 'Devam ediyor',
  ServisKaydiOlusturuldu: 'SK oluşturuldu',
  GorusmeSonlandirildi: 'Sonlandırıldı',
};

const getSmsTipBadge = (tip) => {
  const tipMap = {
    servis_kaydi_acildi: { text: 'SK Açıldı', color: '#3b82f6', bg: '#dbeafe' },
    randevu_olusturuldu: { text: 'Randevu', color: '#10b981', bg: '#d1fae5' },
    randevu_iptal_edildi: { text: 'İptal', color: '#ef4444', bg: '#fee2e2' },
    otp: { text: 'OTP', color: '#8b5cf6', bg: '#ede9fe' },
    servis_kaydi_randevu_detay: { text: 'Detay', color: '#0d9488', bg: '#ccfbf1' },
  };
  return tipMap[tip] || { text: tip || 'SMS', color: '#64748b', bg: '#f1f5f9' };
};

const HubSection = ({ id, title, count, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="gorusme-hub-section gorusme-hub-section--accordion">
      <button
        type="button"
        className="gorusme-hub-section__toggle"
        aria-expanded={open}
        aria-controls={`hub-section-${id}`}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="gorusme-hub-section__toggle-title">
          {title}
          {count != null ? <span className="gorusme-hub-section__count">{count}</span> : null}
        </span>
        <i className={`fas fa-chevron-${open ? 'up' : 'down'}`} aria-hidden />
      </button>
      {open ? (
        <div id={`hub-section-${id}`} className="gorusme-hub-section__body">
          {children}
        </div>
      ) : null}
    </section>
  );
};

const RecordList = ({ items, onKayitGoruntule }) => (
  <ul className="gorusme-hub-list">
    {items.map((t) => (
      <li key={t.id} className="gorusme-hub-item">
        <div className="gorusme-hub-item__main">
          <span className="gorusme-hub-item__title">{t.islemTipi || 'Kayıt'}</span>
          <span className="gorusme-hub-item__meta">
            {t.numara || t.id} · {t.durum ? `${t.durum} · ` : ''}{formatTarih(t.olusturmaTarihi)}
          </span>
        </div>
        <div className="gorusme-hub-item__actions">
          <button type="button" className="secondary-btn secondary-btn--sm" onClick={() => onKayitGoruntule?.(t)}>
            Görüntüle
          </button>
        </div>
      </li>
    ))}
  </ul>
);

const GorusmeHubStep = ({
  musteriId,
  musteri,
  adresler = [],
  acikKayitlar = [],
  gecmisKayitlar = [],
  cihazlar = [],
  yukleniyor,
  gorusmeler = [],
  gorusmelerLoading = false,
  onLoadGorusmeler,
  smsList = [],
  smsLoading = false,
  onLoadSms,
  onKayitGoruntule,
  onYeniKayit,
}) => {
  const [cagriAltSekme, setCagriAltSekme] = useState('gelen');
  const [cagrilarAcik, setCagrilarAcik] = useState(false);
  const [gorusmelerAcik, setGorusmelerAcik] = useState(false);
  const [smsAcik, setSmsAcik] = useState(false);

  const musteriAd = musteri
    ? `${musteri.adi || ''} ${musteri.soyadi || ''}`.trim() || '—'
    : '—';
  const musteriTelefon = musteri?.telefon ? formatTelefonGoster(musteri.telefon) : '—';

  const handleGorusmelerToggle = useCallback(() => {
    setGorusmelerAcik((v) => {
      const next = !v;
      if (next && onLoadGorusmeler) onLoadGorusmeler();
      return next;
    });
  }, [onLoadGorusmeler]);

  const handleSmsToggle = useCallback(() => {
    setSmsAcik((v) => {
      const next = !v;
      if (next && onLoadSms) onLoadSms();
      return next;
    });
  }, [onLoadSms]);

  const handleCagrilarToggle = useCallback(() => {
    setCagrilarAcik((v) => !v);
  }, []);

  const hasActionItems = acikKayitlar.length > 0;

  return (
    <div className="gorusme-hub-step">
      <header className="gorusme-hub-step__header">
        <h3>Müşteri 360°</h3>
        <p>Görüşme sırasında müşterinin tüm kayıtlarına tek ekrandan erişin.</p>
      </header>

      {musteri ? (
        <div className="gorusme-hub-customer-card">
          <div className="gorusme-hub-customer-card__main">
            <div className="gorusme-hub-customer-card__avatar" aria-hidden>
              <i className="fas fa-user" />
            </div>
            <div>
              <h4 className="gorusme-hub-customer-card__name">{musteriAd}</h4>
              <p className="gorusme-hub-customer-card__meta">
                <i className="fas fa-phone" aria-hidden /> {musteriTelefon}
                {musteri.mail ? (
                  <>
                    {' · '}
                    <i className="fas fa-envelope" aria-hidden /> {musteri.mail}
                  </>
                ) : null}
              </p>
            </div>
          </div>
          {musteriId ? (
            <Link
              to={`/customers/${musteriId}/edit`}
              state={{ from: '/service-records/create' }}
              className="secondary-btn secondary-btn--sm gorusme-hub-customer-card__edit"
            >
              Müşteri düzenle
            </Link>
          ) : null}
        </div>
      ) : null}

      {yukleniyor ? (
        <p className="gorusme-hub-step__loading">
          <i className="fas fa-spinner fa-spin" aria-hidden /> Yükleniyor…
        </p>
      ) : (
        <>
          <HubSection id="acik" title="Devam eden servis kayıtları" count={acikKayitlar.length} defaultOpen={acikKayitlar.length > 0}>
            {acikKayitlar.length > 0 ? (
              <RecordList items={acikKayitlar} onKayitGoruntule={onKayitGoruntule} />
            ) : (
              <p className="gorusme-hub-step__empty-inline">Açık servis kaydı yok.</p>
            )}
          </HubSection>

          <HubSection id="gecmis" title="Geçmiş servis kayıtları" count={gecmisKayitlar.length} defaultOpen={false}>
            {gecmisKayitlar.length > 0 ? (
              <RecordList items={gecmisKayitlar} onKayitGoruntule={onKayitGoruntule} />
            ) : (
              <p className="gorusme-hub-step__empty-inline">Geçmiş servis kaydı yok.</p>
            )}
          </HubSection>

          <HubSection id="cihazlar" title="Cihazlar" count={cihazlar.length} defaultOpen={false}>
            {cihazlar.length > 0 ? (
              <div className="gorusme-hub-table-wrap">
                <table className="gorusme-hub-table">
                  <thead>
                    <tr>
                      <th>Ürün</th>
                      <th>Seri No</th>
                      <th>Garanti bitiş</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cihazlar.map((c) => (
                      <tr key={c.id}>
                        <td>{c.icerikAdi || '—'}</td>
                        <td>{c.seriNumarasi || '—'}</td>
                        <td>{formatTarihKisa(c.garantiBitisTarihi)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="gorusme-hub-step__empty-inline">Kayıtlı cihaz yok.</p>
            )}
          </HubSection>

          <HubSection id="adresler" title="Adresler" count={adresler.length} defaultOpen={false}>
            {adresler.length > 0 ? (
              <ul className="gorusme-hub-address-list">
                {adresler.map((a) => (
                  <li key={a.id} className="gorusme-hub-address-item">
                    {a.varsayilan ? <span className="gorusme-hub-address-item__badge">Varsayılan</span> : null}
                    <p className="gorusme-hub-address-item__text">{a.adres}</p>
                    <p className="gorusme-hub-address-item__meta">
                      {[a.districtName, a.cityName].filter(Boolean).join(' / ') || '—'}
                    </p>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="gorusme-hub-step__empty-inline">Kayıtlı adres yok.</p>
            )}
          </HubSection>

          <section className="gorusme-hub-section gorusme-hub-section--accordion">
            <button
              type="button"
              className="gorusme-hub-section__toggle"
              aria-expanded={gorusmelerAcik}
              onClick={handleGorusmelerToggle}
            >
              <span className="gorusme-hub-section__toggle-title">
                Görüşme kayıtları
                {gorusmeler.length > 0 ? <span className="gorusme-hub-section__count">{gorusmeler.length}</span> : null}
              </span>
              <i className={`fas fa-chevron-${gorusmelerAcik ? 'up' : 'down'}`} aria-hidden />
            </button>
            {gorusmelerAcik ? (
              <div className="gorusme-hub-section__body">
                {gorusmelerLoading ? (
                  <p className="gorusme-hub-step__loading-inline"><i className="fas fa-spinner fa-spin" aria-hidden /> Yükleniyor…</p>
                ) : gorusmeler.length > 0 ? (
                  <div className="gorusme-hub-table-wrap">
                    <table className="gorusme-hub-table">
                      <thead>
                        <tr>
                          <th>Tarih</th>
                          <th>Tip</th>
                          <th>Durum</th>
                          <th>Not</th>
                        </tr>
                      </thead>
                      <tbody>
                        {gorusmeler.map((g) => (
                          <tr key={g.id}>
                            <td>{formatTarih(g.olusturmaTarihi || g.cagriBaslangic)}</td>
                            <td>{g.kaynak === 'santral' ? 'Santral' : 'Manuel'}</td>
                            <td>{GORUSME_DURUM_LABEL[g.durum] || g.durum || '—'}</td>
                            <td className="gorusme-hub-table__truncate">{g.notlar || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="gorusme-hub-step__empty-inline">Görüşme kaydı yok.</p>
                )}
              </div>
            ) : null}
          </section>

          <section className="gorusme-hub-section gorusme-hub-section--accordion">
            <button
              type="button"
              className="gorusme-hub-section__toggle"
              aria-expanded={smsAcik}
              onClick={handleSmsToggle}
            >
              <span className="gorusme-hub-section__toggle-title">
                SMS geçmişi
                {smsList.length > 0 ? <span className="gorusme-hub-section__count">{smsList.length}</span> : null}
              </span>
              <i className={`fas fa-chevron-${smsAcik ? 'up' : 'down'}`} aria-hidden />
            </button>
            {smsAcik ? (
              <div className="gorusme-hub-section__body">
                {smsLoading ? (
                  <p className="gorusme-hub-step__loading-inline"><i className="fas fa-spinner fa-spin" aria-hidden /> Yükleniyor…</p>
                ) : smsList.length > 0 ? (
                  <ul className="gorusme-hub-sms-list">
                    {smsList.map((s) => {
                      const badge = getSmsTipBadge(s.tip);
                      return (
                        <li key={s.id || s._id} className="gorusme-hub-sms-item">
                          <div className="gorusme-hub-sms-item__head">
                            <span
                              className="gorusme-hub-sms-item__badge"
                              style={{ color: badge.color, backgroundColor: badge.bg }}
                            >
                              {badge.text}
                            </span>
                            <span className="gorusme-hub-sms-item__date">{formatTarih(s.gonderimTarihi || s.createdAt)}</span>
                          </div>
                          <p className="gorusme-hub-sms-item__text">{s.mesaj || s.icerik || '—'}</p>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="gorusme-hub-step__empty-inline">SMS kaydı yok.</p>
                )}
              </div>
            ) : null}
          </section>

          <section className="gorusme-hub-section gorusme-hub-section--accordion">
            <button
              type="button"
              className="gorusme-hub-section__toggle"
              aria-expanded={cagrilarAcik}
              onClick={handleCagrilarToggle}
            >
              <span className="gorusme-hub-section__toggle-title">Çağrı geçmişi</span>
              <i className={`fas fa-chevron-${cagrilarAcik ? 'up' : 'down'}`} aria-hidden />
            </button>
            {cagrilarAcik ? (
              <div className="gorusme-hub-section__body gorusme-hub-section__body--calls">
                <div className="gorusme-hub-calls-tabs">
                  <button
                    type="button"
                    className={`gorusme-hub-calls-tab${cagriAltSekme === 'gelen' ? ' is-active' : ''}`}
                    onClick={() => setCagriAltSekme('gelen')}
                  >
                    Gelen
                  </button>
                  <button
                    type="button"
                    className={`gorusme-hub-calls-tab${cagriAltSekme === 'giden' ? ' is-active' : ''}`}
                    onClick={() => setCagriAltSekme('giden')}
                  >
                    Giden
                  </button>
                </div>
                {cagriAltSekme === 'gelen' ? (
                  <MusteriCagriListesi
                    tip="gelen"
                    telefon={musteri?.telefon || ''}
                    hideMusteriKolonu
                    hideTelefonFiltresi
                    aktif={cagrilarAcik && cagriAltSekme === 'gelen'}
                  />
                ) : (
                  <MusteriCagriListesi
                    tip="giden"
                    telefon={musteri?.telefon || ''}
                    hideMusteriKolonu
                    hideTelefonFiltresi
                    aktif={cagrilarAcik && cagriAltSekme === 'giden'}
                  />
                )}
              </div>
            ) : null}
          </section>

          {!hasActionItems && !gecmisKayitlar.length && !cihazlar.length ? (
            <p className="gorusme-hub-step__empty">Bu müşteri için açık servis kaydı bulunamadı.</p>
          ) : null}
        </>
      )}

      <footer className="gorusme-hub-step__footer">
        <button type="button" className="primary-btn gorusme-hub-step__new-btn" onClick={onYeniKayit}>
          <i className="fas fa-plus" aria-hidden /> Yeni servis kaydı oluştur
        </button>
      </footer>
    </div>
  );
};

export default GorusmeHubStep;
